export const ARIA_DESC = 'aria-description';
export const ARIA_LABEL = 'aria-label';
export const ARIA_LABELLEDBY = 'aria-labelledby';
export const QUICK_EVENT = { passive: true, capture: true };

import { version } from '../../package.json' with { type: 'json' };

// Using function instead of constant to support environments where DOM can be unloaded (like Vitest with jsdom)
export const isBrowser = () =>
  typeof window !== 'undefined' && typeof document !== 'undefined';

export const isWindows = () =>
  isBrowser() &&
  // @ts-expect-error Typescript has not implemented userAgentData yet https://stackoverflow.com/a/71392474
  /^Win/i.test(navigator.userAgentData?.platform || navigator.platform);

// Make sure we have a HTMLElement to extend (for server side rendering)
export const DSElement =
  typeof HTMLElement === 'undefined'
    ? (class {} as typeof HTMLElement)
    : HTMLElement;

export function debounce<T extends unknown[]>(
  callback: (...args: T) => void,
  delay: number,
) {
  let timer: ReturnType<typeof setTimeout>;

  return function (this: unknown, ...args: T) {
    clearTimeout(timer);
    timer = setTimeout(() => callback.apply(this, args), delay);
  };
}

/**
 * warn
 * @description Utility to console.log, but can be silenced in production with window.dsWarnings = false;
 */
declare global {
  interface Window {
    dsWarnings?: boolean;
  }
}
export const warn = (
  message: string,
  ...args: Parameters<typeof console.log> // Using console.log, not console.warn, to prevent stopping test runners such as Jest
) =>
  !isBrowser() ||
  window.dsWarnings === false ||
  console.log(`\x1B[1mDesignsystemet:\x1B[m ${message}`, ...args);

/**
 * attr
 * @description Utility to quickly get, set and remove attributes
 * @param el The Element to read/write attributes from
 * @param name The attribute name to get, set or remove, or a object to set multiple attributes
 * @param value A valid attribute value or null to remove attribute
 */
export const attr = (
  el: Element,
  name: string,
  value?: string | null,
): string | null => {
  if (value === undefined) return el.getAttribute(name);
  if (value === null) el.removeAttribute(name);
  else if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  return null;
};

/**
 * getCSSProp
 * @description Retrieves and CSS property value and trims it
 * @param el The Element to read attributes/CSS from
 * @param name Attribute or CSS property to get
 * @return string CSS property value
 */
export const getCSSProp = (el: Element, prop: string) =>
  getComputedStyle(el).getPropertyValue(prop).trim();

const QUOTES_STRIP_SURROUNDING = /^["']|["']$/g; // Matches surrounding single or double quotes
const QUOTES_MATCH = /\\(["'])/g;

/**
 * attrOrCSS
 * @description Retrieves and updates attribute based on attribute or CSS property value
 * @param el The Element to read attributes/CSS from
 * @param name Attribute or CSS property to get
 * @return string attribute or CSS property value
 */
export const attrOrCSS = (el: Element, name: string) => {
  let value = attr(el, name);
  if (!value)
    value = getCSSProp(el, `--_ds-${name}`)
      .replace(QUOTES_STRIP_SURROUNDING, '')
      .replace(QUOTES_MATCH, '$1') // Unescape quotes
      .trim();
  return value || null;
};

/**
 * getRoot
 * @description Helper for better compatibility
 * @param node The target node
 * @return The shadow root or document
 */
export const getRoot = (node?: Node | null): Document | ShadowRoot => {
  const root = node?.getRootNode?.() || node?.ownerDocument;
  if (root instanceof Document || root instanceof ShadowRoot) return root;
  return node?.ownerDocument || document;
};

/**
 * getComposedTarget
 * @description Helper to get the composed target of an event, supporting ShadowDOM rendering
 * @param event The event
 * @return The target Element
 */
export const getComposedTarget = (e: Event): Element | null => {
  const el =
    ((e.target as Element)?.shadowRoot && e.composedPath()[0]) || e.target; // Only use composedPath if the target has a shadowRoot to boost performance
  return (el as Node)?.nodeType === 1 ? (el as Element) : null;
};

/**
 * getComposedPath
 * @description Helper to get the full composed path even if listener is bound to a ShadowRoot (unlike event.composedPath())
 * @param el The element to start traversing from
 * @return Set of nodes in the path
 */
export const getComposedPath = (el: Node | null) => {
  const path = new Set<Node>();
  while (el) {
    path.add(el);
    if (el.nodeType === 11) el = (el as ShadowRoot).host;
    else el = (el as Element).assignedSlot || el.parentNode;
  }
  return path;
};

/**
 * on
 * @param el The Element to use as EventTarget
 * @param types A space separated string of event types
 * @param listener An event listener function or listener object
 */
export const on = (
  el: Node | Window | ShadowRoot,
  ...rest: Parameters<typeof Element.prototype.addEventListener>
): (() => void) => {
  const [types, ...options] = rest;
  for (const type of types.split(' ')) el.addEventListener(type, ...options);
  return () => off(el, ...rest);
};

/**
 * off
 * @param el The Element to use as EventTarget
 * @param types A space separated string of event types
 * @param listener An event listener function or listener object
 */
export const off = (
  el: Node | Window | ShadowRoot,
  ...rest: Parameters<typeof Element.prototype.removeEventListener>
): void => {
  const [types, ...options] = rest;
  for (const type of types.split(' ')) el.removeEventListener(type, ...options);
};

// Used to store cleanup functions for hot-reloading
const HOT_RELOAD_KEY = `_dsHotReloadCleanup${version}` as const; // Ensure multiple versions of Designsystemet can run on same page
declare global {
  interface Window {
    [HOT_RELOAD_KEY]?: Map<string, Array<() => void>>;
  }
}

/**
 * onHotReload
 * @description Runs a callback when window is loaded in browser, and ensures cleanup when hot-reloading
 * @param key The key to identify setup and corresponding cleanup
 * @param callback The callback to run when the page is ready
 */
export const onHotReload = (key: string, setup: () => Array<() => void>) => {
  if (!isBrowser()) return; // Skip if not in modern browser environment, but on each call as Vitest might have unloaded jsdom between tests
  if (!window[HOT_RELOAD_KEY]) window[HOT_RELOAD_KEY] = new Map(); // Hot reload cleanup support supporting all build tools
  for (const cleanup of window[HOT_RELOAD_KEY]?.get(key) || []) cleanup(); // Run previous cleanup
  window[HOT_RELOAD_KEY]?.set(key, setup()); // Store new cleanup
};

/**
 * MutationObserver wrapper with automatic cleanup
 * @return new MutaionObserver
 */
export const onMutation = <T extends Node>(
  el: T,
  callback: (el: T, records?: MutationRecord[]) => void,
  options: MutationObserverInit,
) => {
  const cleanup = () => observer.disconnect();
  const observer = new MutationObserver((records) => {
    if (!isBrowser() || !el.isConnected) return cleanup(); // Stop observing if element is removed from DOM or document is removed by jdsom tests
    callback(el, records);
  });

  callback(el); // Initial is run instantly to make test markup predictable
  observer.observe(el, options);
  return cleanup;
};

/**
 * tag
 * @description creates element and assigns properties
 * @param tagName The tagname of element to create
 * @param attrs Optional attributes to add to the element
 * @param text Optional text content to add to the element
 * @return HTMLElement with props
 */
export const tag = <TagName extends keyof HTMLElementTagNameMap>(
  tagName: TagName,
  attrs?: Record<string, string | null> | null,
): HTMLElementTagNameMap[TagName] => {
  const el = document.createElement(tagName);
  if (attrs) for (const [key, val] of Object.entries(attrs)) attr(el, key, val);
  return el;
};

/**
 * customElements.define
 * @description Defines a customElement if running in browser and if not already registered
 * Scoped/named "customElements.define" so @custom-elements-manifest/analyzer can find tag names
 */
export const customElements = {
  define: (name: string, instance: CustomElementConstructor) =>
    !isBrowser() ||
    window.customElements.get(name) ||
    window.customElements.define(name, instance),
};

/**
 * useId
 * @return A generated unique ID
 */
declare global {
  interface Window {
    dsUseId?: number; // Use a global counter to ensure this works even when loading designsystemet multiple times
  }
}
let id = 0;
export function useId(el?: Element | null) {
  if (!isBrowser()) return `:ds:${++id}`; // Emulate browser environment if window not available
  if (!window.dsUseId) window.dsUseId = 0; // Make sure we have a global to support multiple instances of designsystemet in same page
  if (el && !el.id) el.id = `:ds:${++window.dsUseId}`;
  return el?.id || '';
}

/**
 * @description Based off speak function from [U-elements](https://github.com/u-elements/u-elements/blob/main/packages/utils.ts#L210)
 * @param text The text to announce
 */
let LIVE_EL: HTMLElement | undefined;
let LIVE_FIX = 0;
let LIVE_CLEAR: ReturnType<typeof setTimeout> | number = 0;
export const announce = (text: string) => {
  clearTimeout(LIVE_CLEAR);
  if (LIVE_EL) LIVE_EL.textContent = `${text}${LIVE_FIX++ % 2 ? '\u00A0' : ''}`; // Non-breaking space to ensure screen reader announces
  if (text) LIVE_CLEAR = setTimeout(announce, 2000, ''); // Clear prevent old announcements being found by screen readers, with 2 seconds brace period to avoid cutting of Android Talkback
};

// Mount live region on first focus so its ready to be used
const announceMount = () => {
  if (document.readyState !== 'complete') return; // Ensure page is loaded trying to avoid issues with React hydration
  if (!LIVE_EL) {
    LIVE_EL = tag('div', { 'aria-live': 'assertive' });
    LIVE_EL.style.overflow = 'hidden'; // Settings styles individually to prevent issues with CSP
    LIVE_EL.style.position = 'fixed';
    LIVE_EL.style.whiteSpace = 'nowrap';
    LIVE_EL.style.width = '1px';
  }
  if (!LIVE_EL.isConnected) document.body.appendChild(LIVE_EL);
};
onHotReload('announce', () => [
  on(document, 'focus mouseover', announceMount, QUICK_EVENT),
]);
